/**
 * The `siteSearch` Alpine component — live search on the search view
 * (resources/views/pages/search.twig).
 *
 * The server renders the complete results for the URL's ?s= (and
 * ?search-page=), and the form is a plain GET form, so search works with
 * JavaScript off. This component adds the live part: typing waits for a
 * 350ms pause, then fetches GET og/v1/search?s=…&page=N, swaps the innards of
 * [x-ref=content], copies the new status into the role="status" line and
 * replaces ?s= on the URL (a keystroke is not a history entry). A pager click
 * swaps the same way and pushes a history entry, as on the Blog List; Back
 * reloads the results the URL names. A newer load supersedes one in flight.
 * If a pager fetch fails, the browser follows the link; a failed fetch while
 * typing leaves the results as they are.
 *
 * Registration only — app.js starts Alpine (see interactivity.md).
 */
import Alpine from 'alpinejs';

/** The pause after the last keystroke before a search runs. */
const DEBOUNCE_MS = 350;

/** One letter matches nearly everything; wait for two. */
const MIN_LENGTH = 2;

/** The pager's fade-out before the swap, matching blog-pager.css. */
const FADE_MS = 200;

function wait( ms ) {
	return new Promise( ( resolve ) => {
		setTimeout( resolve, ms );
	} );
}

document.addEventListener( 'alpine:init', () => {
	Alpine.data( 'siteSearch', ( config = {} ) => ( {
		// The field's value (x-model).
		term: config.term ?? '',

		// The term and page the results show now.
		shownTerm: config.term ?? '',
		shownPage: 1,

		// The pager fade; typing swaps without it.
		swapping: false,

		// Counts loads. A load whose number is no longer current was superseded.
		request: 0,

		timer: null,

		get contentClass() {
			return { 'is-swapping': this.swapping };
		},

		init() {
			this.shownPage = this.pageFromUrl();

			// Kept so destroy() can remove it when Alpine tears the view down.
			this.onPopstate = () => {
				const url = new URL( window.location.href );
				const term = ( url.searchParams.get( 's' ) ?? '' ).trim();
				const page = this.pageFromUrl();

				if ( term !== this.shownTerm || page !== this.shownPage ) {
					this.term = term;
					this.load( term, page, { history: 'none' } );
				}
			};
			window.addEventListener( 'popstate', this.onPopstate );
		},

		destroy() {
			window.removeEventListener( 'popstate', this.onPopstate );
			clearTimeout( this.timer );
		},

		pageFromUrl() {
			const page = Number.parseInt( new URLSearchParams( window.location.search ).get( config.param ) ?? '1', 10 );

			return page > 0 ? page : 1;
		},

		queue() {
			clearTimeout( this.timer );
			this.timer = setTimeout( () => this.search(), DEBOUNCE_MS );
		},

		search() {
			const term = this.term.trim();

			// One letter keeps the results already shown.
			if ( term.length > 0 && term.length < MIN_LENGTH ) {
				return;
			}

			if ( term === this.shownTerm && this.shownPage === 1 ) {
				return;
			}

			this.load( term, 1, { history: 'replace' } );
		},

		go( event ) {
			const link = event.target.closest( 'a[data-page]' );

			// Leave new-tab and other modified clicks to the browser.
			if (
				! link ||
				event.defaultPrevented ||
				event.button !== 0 ||
				event.metaKey ||
				event.ctrlKey ||
				event.shiftKey ||
				event.altKey
			) {
				return;
			}

			event.preventDefault();

			// Repeat clicks wait for the load in flight; the current page's dot does nothing.
			if ( this.swapping || Number( link.dataset.page ) === this.shownPage ) {
				return;
			}

			this.load( this.shownTerm, Number( link.dataset.page ), { history: 'push', fallback: link.href } );
		},

		/** The address for a term and page: ?s=…, plus ?search-page= past page 1. */
		urlFor( term, page ) {
			const url = new URL( window.location.href );
			url.searchParams.set( 's', term );
			url.searchParams.delete( config.param );

			if ( page > 1 ) {
				url.searchParams.set( config.param, String( page ) );
			}

			return url;
		},

		async load( term, page, { history, fallback } ) {
			const request = ++this.request;
			const paging = 'push' === history;
			this.swapping = paging;

			const endpoint = new URL( config.endpoint, window.location.href );
			endpoint.searchParams.set( 's', term );
			endpoint.searchParams.set( 'page', String( page ) );

			const [ response ] = await Promise.all( [
				fetch( endpoint, { headers: { Accept: 'text/html' } } ).catch( () => null ),
				wait( paging ? FADE_MS : 0 ),
			] );

			const html = response?.ok ? await response.text().catch( () => null ) : null;

			if ( request !== this.request ) {
				return;
			}

			if ( html === null ) {
				this.swapping = false;

				// A pager click follows its link. Typing never navigates (WCAG 3.2.2): the results stay as they are.
				if ( paging ) {
					window.location.assign( fallback ?? this.urlFor( term, page ) );
				}

				return;
			}

			this.$refs.content.innerHTML = html;
			this.$refs.status.textContent = this.$refs.content.firstElementChild?.dataset.status ?? '';
			this.shownTerm = term;
			this.shownPage = page;
			this.swapping = false;

			if ( 'replace' === history ) {
				window.history.replaceState( null, '', this.urlFor( term, page ) );
			}

			if ( paging ) {
				window.history.pushState( null, '', this.urlFor( term, page ) );

				// To the cards, not the field; focus follows, so a screen reader hears the new page.
				this.$refs.content.scrollIntoView( { block: 'start' } );
				this.$refs.content.querySelector( '.blog-list-status' )?.focus( { preventScroll: true } );
			}
		},
	} ) );
} );
